import type {
  GetApiV1ThreeLetterCodeClientAdminsClient200,
  GetApiV1ThreeLetterCodeClientAdminsStripeCreditCard200,
} from '@fukuri-admins/apis/client/gen/model';
import { CARD_BRAND_LABELS, SETTLEMENT_METHOD_LABELS } from '../../constants';
import type { ContractInfoPresentation } from '../../presentations';

type PresentationProps = React.ComponentProps<typeof ContractInfoPresentation>;

type Args = {
  /** 導入企業情報取得 API のレスポンスデータ */
  data: GetApiV1ThreeLetterCodeClientAdminsClient200;
  /** Stripe 既定カード情報。null = 未登録（404） */
  creditCardData: GetApiV1ThreeLetterCodeClientAdminsStripeCreditCard200 | null;
};

// この converter が担うのは API レスポンス由来の表示値のみ。フォームの開閉状態等の
// Props は Component Hook が持つため、Pick で責務範囲を型として明示する
type IConvertToPresentationProps = (
  args: Args
) => Pick<
  PresentationProps,
  | 'companyInfo'
  | 'planInfo'
  | 'contactInfo'
  | 'billingInfo'
  | 'settlementMethodLabel'
  | 'isCardPayment'
  | 'cardLabel'
>;

/**
 * 導入企業情報と Stripe 既定カード情報を ContractInfoPresentation の Props に変換する
 *
 * `private_client` の required は `id` / `name` / `giftee_plan` / `gift_plan` のみで
 * 他は全て nullable なため、未設定値は表示用の '-' に正規化する
 * （既存実装の慣例に合わせ `||` フォールバックを使用する）。
 * カードブランドは未知の値の場合、元の値をそのまま表示する。
 *
 * カード情報の表示可否は `settlement_method` から導出した `isCardPayment` に集約する。
 * カード払い以外（請求書払い / 契約なし）では Stripe Customer が結線されておらず、
 * 既定カード情報 API も SetupIntent 発行 API も 404 を返すため、cardLabel は
 * creditCardData の有無に関わらず undefined を返す（Presentation 側はカード情報行と
 * カード変更フォームを描画しない）。
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({
  data: { client },
  creditCardData,
}) => ({
  companyInfo: {
    corporateNumber: client.corporate_number || '-',
    companyName: client.name,
    companyNameKana: client.name_kana || '-',
    representativeName: client.representative_name || '-',
    address: {
      postalCode: client.postal_code
        ? `${client.postal_code.slice(0, 3)}-${client.postal_code.slice(3)}`
        : '-',
      prefectureCity:
        client.prefecture || client.city
          ? `${client.prefecture?.name ?? ''}${client.city ?? ''}`
          : '-',
      streetAddress: client.street_address || '-',
    },
    partnerBranchName: client.partner_branch?.name || '-',
  },
  planInfo: {
    contractPlanName: client.contract_plan?.name || '-',
    // `contract_started_on` は 'YYYY-MM-DD' 形式の文字列。Date へ変換するとタイムゾーンによって
    // 日付が前日に丸まりうるため、Date を経由せず区切り文字を置換するだけに留める
    contractStartedOnLabel: client.contract_started_on
      ? client.contract_started_on.replaceAll('-', '/')
      : '-',
  },
  contactInfo: {
    contactPersonName: client.contact_person_name || '-',
    contactPersonNameKana: client.contact_person_name_kana || '-',
    contactPersonEmail: client.contact_person_email || '-',
    contactPersonPhone: client.contact_person_phone || '-',
  },
  billingInfo: {
    billingContactName: client.billing_contact_name || '-',
    billingContactNameKana: client.billing_contact_name_kana || '-',
    billingEmail: client.billing_email || '-',
  },
  settlementMethodLabel: client.settlement_method
    ? SETTLEMENT_METHOD_LABELS[client.settlement_method]
    : '-',
  isCardPayment: client.settlement_method === 'card',
  cardLabel:
    client.settlement_method === 'card' && creditCardData
      ? `${CARD_BRAND_LABELS[creditCardData.stripe_credit_card.brand] ?? creditCardData.stripe_credit_card.brand}（‥‥${creditCardData.stripe_credit_card.last4}）`
      : undefined,
});
